import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import AdatisLogo from "../../Components/Common/AdatisLogo";
import { loginSuccess } from "../../slices/auth/login/reducer";
import {
  completeInviteSetup,
  getAuthSession,
} from "../../services/authService";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

const InviteSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordShow, setPasswordShow] = useState(false);

  const emailFromQuery = (searchParams.get("email") || "").trim();
  const tokenFromQuery = (searchParams.get("token") || "").trim();

  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().email().required(t("auth.inviteSetup.validation.emailRequired")),
        password: Yup.string()
          .min(8, t("auth.inviteSetup.validation.passwordMin"))
          .required(t("auth.inviteSetup.validation.passwordRequired")),
        password_confirmation: Yup.string()
          .oneOf([Yup.ref("password")], t("auth.inviteSetup.validation.passwordMatch"))
          .required(t("auth.inviteSetup.validation.passwordConfirmRequired")),
        name: Yup.string().nullable(),
      }),
    [t]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: emailFromQuery,
      password: "",
      password_confirmation: "",
      name: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!tokenFromQuery) {
        setErrorMessage(t("auth.inviteSetup.errors.missingToken"));
        return;
      }

      setLoader(true);
      setErrorMessage("");

      try {
        const response = await completeInviteSetup({
          email: values.email.trim(),
          token: tokenFromQuery,
          password: values.password,
          password_confirmation: values.password_confirmation,
          name: values.name.trim() || undefined,
        });

        if (!response.success || !response.data) {
          setErrorMessage(
            response.error?.message ||
              response.message ||
              t("auth.inviteSetup.errors.generic")
          );
          return;
        }

        if (response.message) {
          toast.success(response.message);
        }

        if (
          "type" in response.data &&
          response.data.type === "membership_selection_required"
        ) {
          navigate("/select-studio");
          return;
        }

        const session = getAuthSession();
        if (session) {
          dispatch(loginSuccess(session));
          navigate("/dashboard");
        }
      } catch (error: unknown) {
        const axiosError = error as {
          response?: { data?: { error?: { message?: string }; message?: string } };
        };
        setErrorMessage(
          axiosError.response?.data?.error?.message ||
            axiosError.response?.data?.message ||
            t("auth.inviteSetup.errors.generic")
        );
      } finally {
        setLoader(false);
      }
    },
  });

  document.title = t("auth.inviteSetup.pageTitle");

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/login" className="d-inline-block auth-logo">
                      <AdatisLogo height={40} />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">{t("auth.inviteSetup.title")}</h5>
                      <p className="text-muted mb-2">{t("auth.inviteSetup.subtitle")}</p>
                      <p className="text-muted fs-13 mb-0">{t("auth.inviteSetup.ownStudioHint")}</p>
                    </div>

                    {!tokenFromQuery ? (
                      <Alert color="warning" className="mt-3">
                        {t("auth.inviteSetup.errors.missingToken")}
                      </Alert>
                    ) : null}

                    {errorMessage ? (
                      <Alert color="danger" className="mt-3">
                        {errorMessage}
                      </Alert>
                    ) : null}

                    <div className="p-2 mt-3">
                      <Form
                        onSubmit={(event) => {
                          event.preventDefault();
                          validation.handleSubmit();
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="invite-email">{t("auth.inviteSetup.fields.email")}</Label>
                          <Input
                            id="invite-email"
                            name="email"
                            type="email"
                            value={validation.values.email}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={!!(validation.touched.email && validation.errors.email)}
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="invite-name">{t("auth.inviteSetup.fields.name")}</Label>
                          <Input
                            id="invite-name"
                            name="name"
                            type="text"
                            value={validation.values.name}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          />
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="invite-password">
                            {t("auth.inviteSetup.fields.password")}
                          </Label>
                          <div className="position-relative auth-pass-inputgroup">
                            <Input
                              id="invite-password"
                              name="password"
                              type={passwordShow ? "text" : "password"}
                              value={validation.values.password}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                !!(validation.touched.password && validation.errors.password)
                              }
                            />
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow((prev) => !prev)}
                            >
                              <i className="ri-eye-fill align-middle" />
                            </button>
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="invite-password-confirmation">
                            {t("auth.inviteSetup.fields.passwordConfirm")}
                          </Label>
                          <Input
                            id="invite-password-confirmation"
                            name="password_confirmation"
                            type="password"
                            value={validation.values.password_confirmation}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(
                                validation.touched.password_confirmation &&
                                validation.errors.password_confirmation
                              )
                            }
                          />
                          {validation.touched.password_confirmation &&
                          validation.errors.password_confirmation ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password_confirmation}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mt-4">
                          <Button color="success" className="w-100" type="submit" disabled={loader}>
                            {loader ? <Spinner size="sm" className="me-2" /> : null}
                            {t("auth.inviteSetup.actions.submit")}
                          </Button>
                        </div>

                        <div className="mt-3 text-center">
                          <Link to="/login" className="fw-semibold text-primary text-decoration-underline">
                            {t("auth.inviteSetup.actions.backToLogin")}
                          </Link>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default InviteSetup;
